import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { iqQuestions, iqCategoryMeta } from '@/mocks/iqTestData';
import type { IqCategory } from '@/mocks/iqTestData';
import { useTestResult } from '@/hooks/useTestResult';
import SaveStatusBanner from '@/components/feature/SaveStatusBanner';

interface Props {
  answers: Record<number, number>;
  onRetake: () => void;
}

// 표준 정규분포 누적확률 근사 (Abramowitz-Stegun)
function normalCdf(z: number): number {
  const t = 1 / (1 + 0.2316419 * Math.abs(z));
  const d = 0.3989422804014327 * Math.exp((-z * z) / 2);
  let p = d * t * (0.31938153 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))));
  if (z > 0) p = 1 - p;
  return p;
}

// 문항 난이도 가중치: 어려운 문항일수록 더 높게 반영해, 쉬운 문항만으로 점수가 부풀려지지 않도록 보정
function getQuestionWeight(id: number): number {
  // 기초 도형 문항 (단순 배열, 순서) → 낮은 가중치
  if (id <= 13) return 0.6;
  // 기초 언어 문항 (알파벳, 방향, 단순 시각 배열) → 낮은 가중치
  if (id >= 65 && id <= 73) return 0.6;
  // 고난도 창의 문항 (정다면체 회전, 시계 수열, 바둑알 귀납·홀짝, 3×3 패턴) → 높은 가중치
  if ((id >= 74 && id <= 88) || (id >= 97 && id <= 100)) return 1.5;
  // 3D 입체·복합 도형 등 고난도 문항 → 높은 가중치
  if ((id >= 22 && id <= 40) || (id >= 93 && id <= 96)) return 1.5;
  // 일반 난이도 (매트릭스 연산, 도형 겹치기, 공간 회전)
  return 1.0;
}

// 추정치 신뢰구간(± IQ) — 단축 온라인 검사 특성상 ±6 정도의 오차를 안내
const IQ_CONFIDENCE = 6;

function computeResult(answers: Record<number, number>) {
  let correctCount = 0;
  let weightedScore = 0;
  let maxWeight = 0;

  const categoryScores: Record<IqCategory, { correct: number; total: number }> = {
    'shape-pattern': { correct: 0, total: 0 },
    number: { correct: 0, total: 0 },
    verbal: { correct: 0, total: 0 },
    'overlap-2d': { correct: 0, total: 0 },
    'shape-rule': { correct: 0, total: 0 },
    'solid-3d': { correct: 0, total: 0 },
  };

  iqQuestions.forEach((q) => {
    const w = getQuestionWeight(q.id);
    maxWeight += w;
    categoryScores[q.category].total += 1;
    if (answers[q.id] === q.answer) {
      correctCount += 1;
      weightedScore += w;
      categoryScores[q.category].correct += 1;
    }
  });

  // 가중 점수를 100점 만점으로 환산 (난이도별 반영)
  const scaled = (weightedScore / maxWeight) * 100;

  // 현실적 보정: 이 검사의 평균(50백분위) 수험자 환산점을 50으로 설정
  // (기존 원점수 평균 55 가정이 과대평가를 유발 → 난이도 반영 후 평균 50·표준편차 13으로 교정)
  const mean = 50;
  const sd = 13;
  const z = (scaled - mean) / sd;
  let iq = Math.round(100 + z * 15);
  iq = Math.max(60, Math.min(160, iq));

  // 상위 백분위는 "화면에 표시되는 최종 IQ"를 기준으로,
  // 표준 지능 분포(평균 100, 표준편차 15)에서 직접 산출한다.
  // 이렇게 해야 표시 IQ와 백분위가 항상 일치하고,
  // IQ 130 → 상위 2.28% / IQ 150 → 상위 0.04% 로 정확히 맞는다.
  const iqZ = (iq - 100) / 15;
  const percentile = (1 - normalCdf(iqZ)) * 100;

  const iqLow = Math.max(60, iq - IQ_CONFIDENCE);
  const iqHigh = Math.min(160, iq + IQ_CONFIDENCE);

  return { correctCount, scaled, iq, iqLow, iqHigh, percentile, categoryScores };
}

// 상위 % 표시 포맷: 큰 값은 정수, 작은 값은 소수로 정밀 표기
function formatTopPercentile(p: number): string {
  if (p >= 10) return `${Math.round(p)}`;
  if (p >= 1) return p.toFixed(1);
  return p >= 0.1 ? p.toFixed(1) : p.toFixed(2);
}

function getClassification(iq: number): { label: string; color: string; bg: string; desc: string } {
  if (iq >= 130) return { label: '매우 우수', color: 'text-primary-700', bg: 'bg-primary-50 border-primary-200', desc: '상위 2% 내외에 해당하는 매우 높은 수준의 인지능력입니다.' };
  if (iq >= 120) return { label: '우수', color: 'text-primary-700', bg: 'bg-primary-50 border-primary-200', desc: '평균보다 뚜렷하게 높은 우수한 수준입니다.' };
  if (iq >= 110) return { label: '평균 상', color: 'text-accent-700', bg: 'bg-accent-50 border-accent-200', desc: '평균보다 높은 수준입니다.' };
  if (iq >= 90) return { label: '평균', color: 'text-secondary-700', bg: 'bg-secondary-50 border-secondary-200', desc: '대다수 사람들이 속하는 평균 범위입니다.' };
  if (iq >= 80) return { label: '평균 하', color: 'text-accent-700', bg: 'bg-accent-50 border-accent-200', desc: '평균보다 다소 낮은 수준입니다.' };
  if (iq >= 70) return { label: '경계선', color: 'text-foreground-600', bg: 'bg-background-100 border-background-200', desc: '평균과 낮음의 경계에 있는 수준입니다.' };
  return { label: '매우 낮음', color: 'text-foreground-600', bg: 'bg-background-100 border-background-200', desc: '인지능력 발달에 대한 추가적인 확인이 권장됩니다.' };
}

const categoryBarColor: Record<IqCategory, string> = {
  'shape-pattern': 'bg-primary-500',
  number: 'bg-accent-500',
  verbal: 'bg-secondary-500',
  'overlap-2d': 'bg-primary-400',
  'shape-rule': 'bg-secondary-400',
  'solid-3d': 'bg-accent-400',
};

export default function IqResult({ answers, onRetake }: Props) {
  const navigate = useNavigate();
  const { correctCount, scaled, iq, iqLow, iqHigh, percentile, categoryScores } = computeResult(answers);
  const cls = getClassification(iq);
  const { saveTestResult, saveStatus } = useTestResult();

  useEffect(() => {
    saveTestResult('iq', '종합 인지능력(IQ) 검사', iq, {
      correctCount,
      scaled: Math.round(scaled),
      iqRange: [iqLow, iqHigh],
      percentile,
      categoryScores,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-2xl mx-auto">
      {/* Result Header */}
      <div className="bg-primary-500 rounded-2xl p-6 md:p-8 text-white text-center mb-6">
        <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-2xl mx-auto mb-4">
          <i className="ri-trophy-line text-2xl text-white"></i>
        </div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2">
          검사 결과
        </h1>
        <p className="text-primary-100 text-sm">
          종합 인지능력(IQ) 추정치
        </p>
      </div>

      <SaveStatusBanner status={saveStatus} />

      {/* IQ Score */}
      <div className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8 text-center mb-4">
        <div className="text-6xl md:text-7xl font-bold text-foreground-950 mb-1">
          {iq}
        </div>
        <div className="text-foreground-500 text-sm mb-3">
          추정 범위 <span className="font-semibold text-foreground-700">{iqLow} – {iqHigh}</span>
        </div>
        <div className={`inline-block text-sm font-bold px-4 py-1.5 rounded-full border ${cls.bg} ${cls.color} mb-3`}>
          {cls.label}
        </div>
        <p className="text-foreground-600 text-sm mb-4">{cls.desc}</p>

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-background-100 border border-background-200 rounded-xl p-4">
            <div className="text-2xl font-bold text-foreground-950">{correctCount}<span className="text-sm text-foreground-500"> / {iqQuestions.length}</span></div>
            <div className="text-xs text-foreground-500 mt-1">정답 문항 수</div>
          </div>
          <div className="bg-background-100 border border-background-200 rounded-xl p-4">
            <div className="text-2xl font-bold text-foreground-950">{iq >= 100 ? '상위' : '하위'} {formatTopPercentile(percentile)}%</div>
            <div className="text-xs text-foreground-500 mt-1">{iq >= 100 ? '백분위 (추정)' : '당신보다 높은 점수의 비율'}</div>
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-4">
        <h2 className="font-semibold text-foreground-950 mb-4 flex items-center gap-2">
          <i className="ri-bar-chart-2-line text-primary-500"></i>
          영역별 분석
        </h2>
        <div className="space-y-4">
          {iqCategoryMeta.map((cat) => {
            const score = categoryScores[cat.id];
            const pct = score.total > 0 ? (score.correct / score.total) * 100 : 0;
            return (
              <div key={cat.id}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <i className={`${cat.icon} text-foreground-500 text-sm`}></i>
                    <span className="text-sm text-foreground-700">{cat.label}</span>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">
                    {score.correct}<span className="text-foreground-400 text-xs"> / {score.total}</span>
                  </span>
                </div>
                <div className="w-full bg-background-200 rounded-full h-2.5">
                  <div
                    className={`${categoryBarColor[cat.id]} h-2.5 rounded-full transition-all duration-500`}
                    style={{ width: `${pct}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* IQ 기준 */}
      <div className="bg-accent-50 border border-accent-200 rounded-xl p-4 mb-6">
        <div className="flex gap-2">
          <i className="ri-information-line text-accent-500 flex-shrink-0 mt-0.5"></i>
          <p className="text-accent-700 text-sm leading-relaxed">
            <strong>해석 기준:</strong> 90~109 평균 · 110~119 평균 상 · 120~129 우수 · 130 이상 매우 우수.
            문항 난이도를 반영해 산출했으며, 점수는 참고용 추정치로 실제 지능은 표시된 범위(±6) 내에 있을 가능성이 높습니다. 정식 지능검사를 대체할 수 없습니다.
          </p>
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-primary-50 border border-primary-200 rounded-xl p-5 mb-6">
        <h3 className="font-semibold text-primary-800 mb-3">
          <i className="ri-arrow-right-circle-line mr-2"></i>다음 단계
        </h3>
        <div className="space-y-2">
          <button
            onClick={() => navigate('/study-style')}
            className="whitespace-nowrap w-full text-left text-sm text-primary-700 bg-white border border-primary-200 rounded-lg px-4 py-2.5 hover:bg-primary-100 cursor-pointer flex items-center gap-2"
          >
            <i className="ri-brain-line"></i>
            나에게 맞는 공부법 찾기
          </button>
          <button
            onClick={() => navigate('/career-test')}
            className="whitespace-nowrap w-full text-left text-sm text-primary-700 bg-white border border-primary-200 rounded-lg px-4 py-2.5 hover:bg-primary-100 cursor-pointer flex items-center gap-2"
          >
            <i className="ri-compass-3-line"></i>
            진로·계열 탐색 검사하기
          </button>
        </div>
      </div>

      {/* Retake Button */}
      <button
        onClick={onRetake}
        className="whitespace-nowrap w-full py-3.5 rounded-xl border border-background-300 text-foreground-600 font-semibold text-sm hover:bg-background-100 transition-colors cursor-pointer"
      >
        <i className="ri-refresh-line mr-2"></i>다시 검사하기
      </button>
    </div>
  );
}